import React from 'react';
import CSSZSickLeaveCertificateFormHeadline from './CSSZSickLeaveCertificateFormHeadline';
import Button from '../../../../../Components/Button/Button';
import { useDisclose } from '../../../../Modal/Hooks/useDisclose';
import { DeepPartial } from '../../../../../Interfaces/DeepPartial';
import {
	CSSZSickLeaveValues,
	CSSZSubmitValues,
} from '../CSSZSickLeaveCertificateCreate';
import CSSZSickLeaveCertificateEmploymentFormModal from '../../CSSZSickLeaveCertificateEmploymentManual/CSSZSickLeaveCertificateEmploymentManualModal';
import {
	CSSZAddress,
	CSSZEmployment,
} from '../../../Interfaces/CSSZ.interface';
import Text from '../../../../../Components/Base/Text/Text';
import CSSZAddressPreview from './CSSZAddressPreview';
import FormattedMomentDate, {
	DateFormat,
} from '../../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import { getMomentDate } from '../../../../../Utils/dates.util';
import { Theme } from '../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../Components/Button/LinkButton';
import styles from './CSSZSickLeaveCertificateFormPartC.module.scss';
import InputField from '../../../../../Components/Form/components/InputField';
import CSSZInsuranceAdministratorSelectField from '../../../../Base/Components/Fields/CSSZInsuranceAdministratorSelect/CSSZInsuranceAdministratorSelectField';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import { TermItem } from '../../../../../graphql/graphql';

export interface CSSZSickLeaveCertificateFormPartCProps {
	patientId: string;
	collectedValues?: DeepPartial<CSSZSickLeaveValues>;
	onSubmit: (values: CSSZSubmitValues) => void;
	showProfessionInput?: boolean;
	onInsuranceAdministratorChange: (
		value: SelectValue,
		termItem?: TermItem,
	) => void | Promise<void>;
}

const CSSZSickLeaveCertificateFormPartC = ({
	patientId,
	collectedValues,
	onSubmit,
	showProfessionInput = false,
	onInsuranceAdministratorChange,
}: CSSZSickLeaveCertificateFormPartCProps) => {
	const { isOpen, onOpen, onClose } = useDisclose();

	const handleSubmit = (values: CSSZEmployment) => {
		onSubmit({ type: 'employment', data: values });
		onClose();
	};

	let content = (
		<Button
			title='component.CSSZSickLeaveCertificateFormPartC.entry'
			onClick={onOpen}
			fitContent
		/>
	);

	const employment = collectedValues?.employment;

	if (employment) {
		content = (
			<div className={styles.wrapper}>
				<div>
					<div className={styles.employer}>
						{employment.employer && (
							<Text tx={employment.employer} className={styles.bigText} />
						)}
						{employment.typeOfEmployment && (
							<>
								{' '}
								-{' '}
								<Text
									tx={employment.typeOfEmployment.label}
									className={styles.smallText}
								/>
							</>
						)}
					</div>
					<div className={styles.inline}>
						{employment.validFrom && (
							<div className={styles.smallText}>
								<Text
									tx='component.CSSZSickLeaveCertificateFormPartC.validFrom'
									inline
									colon
								/>{' '}
								<FormattedMomentDate
									date={getMomentDate(employment.validFrom)}
									format={DateFormat.DATE}
								/>
							</div>
						)}
						{employment.validTo && (
							<div className={styles.smallText}>
								<Text
									tx='component.CSSZSickLeaveCertificateFormPartC.validTo'
									inline
									colon
								/>{' '}
								<FormattedMomentDate
									date={getMomentDate(employment.validTo)}
									format={DateFormat.DATE}
								/>
							</div>
						)}
						{employment.note && (
							<Text
								className={styles.smallText}
								tx='component.CSSZSickLeaveCertificateFormPartC.note'
								txParams={{ note: employment.note }}
							/>
						)}
					</div>
					<CSSZAddressPreview address={employment.address as CSSZAddress} />
					{(showProfessionInput || !!employment.profession?.trim()) && (
						<div className={styles.addressLike}>
							<Text
								className={styles.smallerText}
								tx='component.CSSZSickLeaveCertificateFormPartC.professionLabel'
							/>
							{showProfessionInput ? (
								<InputField name='profession' isRequired isWrapped />
							) : (
								<div className={styles.addressText}>
									{employment.profession}
								</div>
							)}
						</div>
					)}
				</div>
				<LinkButton
					title='base.shared.edit'
					theme={Theme.BLUE}
					onClick={onOpen}
				/>
			</div>
		);
	}

	return (
		<>
			<CSSZSickLeaveCertificateFormHeadline title='component.CSSZSickLeaveCertificateFormPartC.title' />
			<div className={styles.insuranceBlock}>
				<CSSZInsuranceAdministratorSelectField
					name='insuranceAdministrator'
					label='component.CSSZSickLeaveCertificateForm.insuranceAdministrator'
					onChange={onInsuranceAdministratorChange}
					isRequired
					isWrapped
				/>
			</div>
			{content}
			{isOpen && (
				<CSSZSickLeaveCertificateEmploymentFormModal
					isOpen
					patientId={patientId}
					onClose={onClose}
					collectedValues={collectedValues}
					onSubmit={handleSubmit}
				/>
			)}
		</>
	);
};

export default CSSZSickLeaveCertificateFormPartC;
